import { PersonFormData, RelationshipFormData, ValidationResult } from '../types';

/**
 * ValidationService - Centralized validation logic for forms and data
 * Provides comprehensive validation rules with detailed error messages
 */
export class ValidationService {
  
  // Common validation patterns
  private static readonly PATTERNS = {
    name: /^[a-zA-Z\s\-'\.À-ÿ]+$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^[\+]?[1-9][\d]{0,15}$/,
    date: /^\d{4}-\d{2}-\d{2}$/,
    year: /^\d{4}$/,
  };

  private static readonly MESSAGES = {
    required: 'This field is required',
    invalidFormat: 'Invalid format',
    tooShort: 'Too short',
    tooLong: 'Too long',
    invalidDate: 'Invalid date',
    futureDate: 'Date cannot be in the future',
    pastDate: 'Date cannot be in the past',
    invalidAge: 'Invalid age range',
    invalidName: 'Name must contain only letters, spaces, hyphens, apostrophes, and periods',
    invalidEmail: 'Invalid email format',
    invalidPhone: 'Invalid phone number format',
  };

  /**
   * Validate person form data
   */
  static validatePersonForm(data: PersonFormData): ValidationResult {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!this.isRequired(data.firstName)) {
      errors.firstName = this.MESSAGES.required;
    } else if (!this.isValidName(data.firstName)) {
      errors.firstName = this.MESSAGES.invalidName;
    } else if (data.firstName.length > 50) {
      errors.firstName = 'First name must be 50 characters or less';
    }

    if (!this.isRequired(data.lastName)) {
      errors.lastName = this.MESSAGES.required;
    } else if (!this.isValidName(data.lastName)) {
      errors.lastName = this.MESSAGES.invalidName;
    } else if (data.lastName.length > 50) {
      errors.lastName = 'Last name must be 50 characters or less';
    }

    if (!data.gender) {
      errors.gender = this.MESSAGES.required;
    }

    // Optional field validation
    if (data.maidenName && !this.isValidName(data.maidenName)) {
      errors.maidenName = this.MESSAGES.invalidName;
    }

    if (data.middleName && !this.isValidName(data.middleName)) {
      errors.middleName = this.MESSAGES.invalidName;
    }

    if (data.suffix && data.suffix.length > 10) {
      errors.suffix = 'Suffix must be 10 characters or less';
    }

    // Date validation
    if (data.birthDate) {
      const birthDateError = this.validateDate(data.birthDate, 'birth');
      if (birthDateError) {
        errors.birthDate = birthDateError;
      }
    }

    if (data.deathDate) {
      const deathDateError = this.validateDate(data.deathDate, 'death');
      if (deathDateError) {
        errors.deathDate = deathDateError;
      }
    }

    // Cross-field validation
    if (data.birthDate && data.deathDate) {
      const birthDate = new Date(data.birthDate);
      const deathDate = new Date(data.deathDate);
      
      if (deathDate <= birthDate) {
        errors.deathDate = 'Death date must be after birth date';
      }

      // Check for reasonable age at death
      const ageAtDeath = deathDate.getFullYear() - birthDate.getFullYear();
      if (ageAtDeath > 150) {
        errors.deathDate = 'Age at death seems unreasonable (over 150 years)';
      }
    }

    // Death status consistency
    if (data.isDeceased && !data.deathDate) {
      errors.deathDate = 'Death date is required when person is marked as deceased';
    }

    if (!data.isDeceased && data.deathDate) {
      errors.isDeceased = 'Person should be marked as deceased if death date is provided';
    }

    // Text field length validation
    if (data.occupation && data.occupation.length > 100) {
      errors.occupation = 'Occupation must be 100 characters or less';
    }

    if (data.education && data.education.length > 200) {
      errors.education = 'Education must be 200 characters or less';
    }

    if (data.biography && data.biography.length > 2000) {
      errors.biography = 'Biography must be 2000 characters or less';
    }

    if (data.personalNotes && data.personalNotes.length > 1000) {
      errors.personalNotes = 'Personal notes must be 1000 characters or less';
    }

    // Nicknames validation
    if (data.nicknames) {
      const nicknames = data.nicknames.split(',').map((n: string) => n.trim()).filter((n: string) => n);
      for (const nickname of nicknames) {
        if (!this.isValidName(nickname)) {
          errors.nicknames = 'All nicknames must contain only letters, spaces, hyphens, apostrophes, and periods';
          break;
        }
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Validate relationship form data
   */
  static validateRelationshipForm(data: RelationshipFormData): ValidationResult {
    const errors: Record<string, string> = {};

    // Required fields
    if (!data.personId1) {
      errors.personId1 = this.MESSAGES.required;
    }

    if (!data.personId2) {
      errors.personId2 = this.MESSAGES.required;
    }

    if (!data.type) {
      errors.type = this.MESSAGES.required;
    }

    // Cannot relate person to themselves
    if (data.personId1 === data.personId2) {
      errors.personId2 = 'Cannot create relationship between person and themselves';
    }

    // Date validation for marriage relationships
    if (data.type === 'spouse') {
      if (data.marriageDate) {
        const marriageDateError = this.validateDate(data.marriageDate, 'marriage');
        if (marriageDateError) {
          errors.marriageDate = marriageDateError;
        }
      }

      if (data.divorceDate) {
        const divorceDateError = this.validateDate(data.divorceDate, 'divorce');
        if (divorceDateError) {
          errors.divorceDate = divorceDateError;
        }
      }

      // Cross-field validation for marriage dates
      if (data.marriageDate && data.divorceDate) {
        const marriageDate = new Date(data.marriageDate);
        const divorceDate = new Date(data.divorceDate);
        
        if (divorceDate <= marriageDate) {
          errors.divorceDate = 'Divorce date must be after marriage date';
        }
      }
    }

    // Adoption date validation
    if (data.adoptionDate) {
      const adoptionDateError = this.validateDate(data.adoptionDate, 'adoption');
      if (adoptionDateError) {
        errors.adoptionDate = adoptionDateError;
      }
    }

    // Notes validation
    if (data.notes && data.notes.length > 500) {
      errors.notes = 'Notes must be 500 characters or less';
    }

    // Sources validation
    if (data.sources) {
      const sources = data.sources.split(',').map((s: string) => s.trim()).filter((s: string) => s);
      if (sources.length > 10) {
        errors.sources = 'Maximum 10 sources allowed';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Validate email format
   */
  static validateEmail(email: string): string | null {
    if (!email) return null;
    
    if (!this.PATTERNS.email.test(email)) {
      return this.MESSAGES.invalidEmail;
    }

    if (email.length > 254) {
      return 'Email must be 254 characters or less';
    }

    return null;
  }

  /**
   * Validate phone number format
   */
  static validatePhone(phone: string): string | null {
    if (!phone) return null;
    
    // Remove common formatting characters
    const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
    
    if (!this.PATTERNS.phone.test(cleanPhone)) {
      return this.MESSAGES.invalidPhone;
    }

    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      return 'Phone number must be between 7 and 15 digits';
    }

    return null;
  }

  /**
   * Validate required field
   */
  private static isRequired(value: any): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  }

  /**
   * Validate name format (letters, spaces, hyphens, apostrophes, periods)
   */
  private static isValidName(name: string): boolean {
    if (!name) return true; // Optional fields can be empty
    return this.PATTERNS.name.test(name);
  }

  /**
   * Validate date format and constraints
   */
  private static validateDate(dateString: string, type: 'birth' | 'death' | 'marriage' | 'divorce' | 'adoption'): string | null {
    if (!dateString) return null;

    // Check basic format
    if (!this.PATTERNS.date.test(dateString)) {
      return 'Date must be in YYYY-MM-DD format';
    }

    const date = new Date(dateString);
    const now = new Date();

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return this.MESSAGES.invalidDate;
    }

    // Check year range (reasonable historical range)
    const year = date.getFullYear();
    if (year < 1600 || year > now.getFullYear() + 1) {
      return 'Year must be between 1600 and current year';
    }

    // Type-specific validation
    switch (type) {
      case 'birth':
        if (date > now) {
          return 'Birth date cannot be in the future';
        }
        // Check for reasonable age (not older than 150 years)
        const age = now.getFullYear() - year;
        if (age > 150) {
          return 'Birth date seems too far in the past (over 150 years ago)';
        }
        break;

      case 'death':
        if (date > now) {
          return 'Death date cannot be in the future';
        }
        break;

      case 'marriage':
      case 'divorce':
      case 'adoption':
        // Allow future dates for these with a warning rather than error
        break;
    }

    return null;
  }

  /**
   * Validate password strength
   */
  static validatePassword(password: string): {
    isValid: boolean;
    errors: string[];
    strength: 'weak' | 'medium' | 'strong';
  } {
    const errors: string[] = [];
    let score = 0;

    if (!password || password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    } else {
      score += 1;
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    } else {
      score += 1;
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    } else {
      score += 1;
    }

    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    } else {
      score += 1;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    } else {
      score += 1;
    }

    if (password.length > 20) {
      score += 1;
    }

    let strength: 'weak' | 'medium' | 'strong';
    if (score < 3) strength = 'weak';
    else if (score < 5) strength = 'medium';
    else strength = 'strong';

    return {
      isValid: errors.length === 0,
      errors,
      strength,
    };
  }

  /**
   * Sanitize input text to prevent XSS
   */
  static sanitizeText(text: string): string {
    if (!text) return '';
    
    return text
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;')
      .trim();
  }

  /**
   * Validate file upload
   */
  static validateFile(file: File, options: {
    maxSize?: number; // in bytes
    allowedTypes?: string[];
    allowedExtensions?: string[];
  }): string | null {
    if (!file) return 'No file selected';

    // Check file size
    if (options.maxSize && file.size > options.maxSize) {
      const maxSizeMB = Math.round(options.maxSize / (1024 * 1024));
      return `File size must be less than ${maxSizeMB}MB`;
    }

    // Check file type
    if (options.allowedTypes && !options.allowedTypes.includes(file.type)) {
      return `File type not allowed. Allowed types: ${options.allowedTypes.join(', ')}`;
    }

    // Check file extension
    if (options.allowedExtensions) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !options.allowedExtensions.includes(fileExtension)) {
        return `File extension not allowed. Allowed extensions: ${options.allowedExtensions.join(', ')}`;
      }
    }

    return null;
  }

  /**
   * Get validation summary for forms
   */
  static getValidationSummary(errors: Record<string, string>): {
    hasErrors: boolean;
    errorCount: number;
    firstError: string | null;
    errorFields: string[];
  } {
    const errorFields = Object.keys(errors);
    
    return {
      hasErrors: errorFields.length > 0,
      errorCount: errorFields.length,
      firstError: errorFields.length > 0 ? errors[errorFields[0]] : null,
      errorFields,
    };
  }

  /**
   * Validate phone number (E.164 format)
   *
   * @param {string} phone - Phone number to validate
   * @returns {string | null} Error message or null if valid
   *
   * @example
   * const error = ValidationService.validatePhoneNumber('+12345678900');
   * if (error) console.log(error); // null - valid
   *
   * const error2 = ValidationService.validatePhoneNumber('123-456-7890');
   * if (error2) console.log(error2); // 'Phone number must be in E.164 format...'
   */
  static validatePhoneNumber(phone: string): string | null {
    if (!phone) return null;

    // E.164 format: +[country code][number]
    // Must start with +, followed by 1-3 digit country code and up to 15 total digits
    const e164Regex = /^\+[1-9]\d{1,14}$/;

    if (!e164Regex.test(phone)) {
      return 'Phone number must be in E.164 format (e.g., +12345678900)';
    }

    return null;
  }

  /**
   * Validate bio text
   *
   * @param {string} bio - Bio text to validate
   * @returns {string | null} Error message or null if valid
   *
   * @example
   * const error = ValidationService.validateBio('Short bio');
   * if (error) console.log(error); // null - valid
   *
   * const longBio = 'a'.repeat(501);
   * const error2 = ValidationService.validateBio(longBio);
   * if (error2) console.log(error2); // 'Bio must be 500 characters or less'
   */
  static validateBio(bio: string): string | null {
    if (!bio) return null;

    // Check length
    if (bio.length > 500) {
      return 'Bio must be 500 characters or less';
    }

    // Check for excessive special characters (potential spam)
    const specialChars = bio.match(/[^a-zA-Z0-9\s.,!?'-]/g);
    if (specialChars && specialChars.length > bio.length * 0.3) {
      return 'Bio contains too many special characters';
    }

    return null;
  }
}

export default ValidationService;